import { useMemo, useState } from "react";
import { buildAdvancedCurve, type AdvancedDoseInput } from "@/domain/advancedCurveEngine";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QuickIngestionWizard } from "@/components/clinical/QuickIngestionWizard";
import { Activity, ChevronRight, Clock3, Maximize2, Plus, Sparkles } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function formatTime(value: string | number | Date) {
  return new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function elapsed(value: string) {
  const deltaMin = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (deltaMin < 60) return `${deltaMin} min atrás`;
  const hours = Math.floor(deltaMin / 60);
  const minutes = deltaMin % 60;
  if (hours < 48) return minutes ? `${hours} h ${minutes} min atrás` : `${hours} h atrás`;
  const days = Math.floor(hours / 24);
  return `${days} dia${days === 1 ? "" : "s"} atrás`;
}

function substanceName(dose: any) {
  return dose.substance_name ?? dose.substances?.name ?? "Substância";
}

function doseLabel(dose: any) {
  if (dose.dose_amount == null) return dose.dose_text ?? "dose não informada";
  return `${dose.dose_amount}${dose.dose_unit ? ` ${dose.dose_unit}` : ""}`;
}

function stomachLabel(dose: any) {
  const value = String(dose.stomach ?? "").toLowerCase();
  if (!value || value === "desconhecido") return null;
  if (value.includes("quarter")) return "¼ cheio";
  if (value.includes("half")) return "½ cheio";
  if (value.includes("full") || value.includes("cheio")) return "cheio";
  if (value.includes("vazio") || value.includes("empty")) return "vazio";
  return value;
}

function JournalTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="min-w-[210px] rounded-xl border bg-card/95 p-3 text-xs shadow-xl backdrop-blur">
      <div className="font-semibold">{row?.label ?? "Horário"}</div>
      <div className="mt-1 flex justify-between gap-4 text-muted-foreground"><span>Carga relativa</span><span className="font-mono text-foreground">{Number(row?.total ?? 0).toFixed(0)}/100</span></div>
      <div className="mt-2 space-y-1">
        {payload.filter((item: any) => item.dataKey !== "total").map((item: any) => (
          <div key={item.dataKey} className="grid grid-cols-[8px_1fr_auto] items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: item.color }} />
            <span className="truncate">{item.name}</span>
            <span className="font-mono">{Number(item.value ?? 0).toFixed(0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SessionJournalPanel({
  doses,
  patientId,
  sessionId,
  title,
  onSaved,
  referenceNow,
  initialWindowHours = 18,
}: {
  doses: AdvancedDoseInput[];
  patientId: string;
  sessionId?: string | null;
  title?: string;
  onSaved?: () => void;
  referenceNow?: Date;
  initialWindowHours?: number;
}) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [windowHours, setWindowHours] = useState(initialWindowHours);
  const [expanded, setExpanded] = useState(false);
  const reference = referenceNow ?? new Date();
  const referenceMs = reference.getTime();
  const result = useMemo(
    () => buildAdvancedCurve(doses, { windowHours, stepMin: 10, now: reference }),
    [doses, windowHours, referenceMs],
  );
  const sortedDoses = useMemo(() => [...(doses as any[])].sort((a, b) => new Date(a.actual_time ?? a.actualTime).getTime() - new Date(b.actual_time ?? b.actualTime).getTime()), [doses]);
  const nowPoint = useMemo(() => result.points.reduce((closest, row) => Math.abs(Number(row.timestamp) - referenceMs) < Math.abs(Number(closest.timestamp) - referenceMs) ? row : closest, result.points[0] ?? { timestamp: referenceMs }), [result.points, referenceMs]);
  const plottedDoses = sortedDoses.filter((dose) => {
    const time = new Date(dose.actual_time ?? dose.actualTime).getTime();
    const first = Number(result.points[0]?.timestamp ?? 0);
    const last = Number(result.points.at(-1)?.timestamp ?? Number.MAX_SAFE_INTEGER);
    return time >= first && time <= last;
  });
  const referenceLabel = referenceNow ? "Referência" : "Agora";

  return (
    <Card className="overflow-hidden rounded-3xl border-primary/20 bg-card shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4 sm:p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><h2 className="font-semibold">{title ?? "Sessão · gráfico rápido"}</h2></div>
          <p className="mt-1 text-xs text-muted-foreground">Visão simples estilo Journal/PsyLog. Curva relativa, não concentração sérica medida.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-md border border-input bg-background px-2 text-xs" value={windowHours} onChange={(event) => setWindowHours(Number(event.target.value))}>
            <option value={12}>12 h</option><option value={18}>18 h</option><option value={24}>24 h</option><option value={28}>28 h</option><option value={36}>36 h</option><option value={48}>48 h</option>
          </select>
          <Button type="button" variant="outline" size="sm" onClick={() => setExpanded((value) => !value)}><Maximize2 className="mr-1 h-4 w-4" />{expanded ? "Compactar" : "Expandir"}</Button>
        </div>
      </div>

      {!doses.length ? (
        <div className="p-6 text-center">
          <Activity className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="mt-3 font-medium">Nenhuma ingestão nesta sessão</div>
          <div className="mt-1 text-sm text-muted-foreground">Registre a primeira dose para gerar a curva e a linha do tempo.</div>
          <Button className="mt-4 rounded-2xl" onClick={() => setWizardOpen(true)}><Plus className="mr-2 h-4 w-4" />Adicionar ingestão</Button>
        </div>
      ) : (
        <>
          <div className="p-3 sm:p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge variant="outline">{referenceLabel} {result.now.total.toFixed(0)}/100 · {result.now.phase}</Badge>
              {result.series.map((series) => <Badge key={series.key} variant="secondary" className="font-normal"><span className="mr-1.5 h-2 w-2 rounded-full" style={{ background: series.color }} />{series.label} {series.nowValue.toFixed(0)}</Badge>)}
            </div>
            <div className="rounded-2xl border bg-muted/10 p-2" style={{ height: expanded ? 470 : 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={result.points} margin={{ top: 12, right: 8, bottom: 4, left: -22 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="timestamp" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={(value) => formatTime(value)} tick={{ fontSize: 11 }} minTickGap={28} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip content={<JournalTooltip />} />
                  {result.series.map((series) => <Area key={series.key} type="monotone" dataKey={series.key} name={series.label} stroke={series.color} fill={series.color} fillOpacity={0.12} strokeWidth={3} dot={false} activeDot={{ r: 5 }} />)}
                  <ReferenceLine x={Number(nowPoint.timestamp)} stroke="hsl(var(--foreground))" strokeWidth={2} label={{ value: referenceNow ? "referência" : "agora", position: "insideTopRight", fill: "hsl(var(--foreground))", fontSize: 10 }} />
                  {plottedDoses.map((dose: any, index) => {
                    const time = new Date(dose.actual_time ?? dose.actualTime).getTime();
                    const series = result.series.find((item) => item.canonicalKey && substanceName(dose).toLowerCase().includes(item.canonicalKey.slice(0, 5))) ?? result.series[index % Math.max(1, result.series.length)];
                    return <ReferenceDot key={dose.id ?? `${time}-${index}`} x={time} y={4 + (index % 3) * 4} r={5} fill={series?.color ?? "hsl(var(--primary))"} stroke="hsl(var(--background))" strokeWidth={2} />;
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {result.notes.length ? <div className="mt-3 rounded-xl border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground">{result.notes.slice(0, 3).map((note) => <div key={note}>{note}</div>)}</div> : null}
          </div>

          <div className="border-t">
            <div className="flex items-center justify-between px-4 py-3 sm:px-5"><div><div className="font-medium">Ingestões</div><div className="text-xs text-muted-foreground">{sortedDoses.length} registro(s) · toque em + para adicionar outra ingestão</div></div><Button className="h-12 w-12 rounded-full p-0 shadow-lg" onClick={() => setWizardOpen(true)} aria-label="Adicionar ingestão"><Plus className="h-6 w-6" /></Button></div>
            <div className="divide-y">
              {[...sortedDoses].reverse().map((dose: any, index) => {
                const matchingSeries = result.series.find((series) => substanceName(dose).toLowerCase().includes(series.canonicalKey.slice(0, 5))) ?? result.series[index % Math.max(1, result.series.length)];
                const stomach = stomachLabel(dose);
                return (
                  <div key={dose.id ?? index} className="flex gap-3 px-4 py-3 sm:px-5">
                    <span className="mt-1 h-10 w-1.5 shrink-0 rounded-full" style={{ background: matchingSeries?.color ?? "hsl(var(--primary))" }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2"><div className="font-medium">{substanceName(dose)}</div><div className="text-xs text-muted-foreground">{formatTime(dose.actual_time ?? dose.actualTime)}</div></div>
                      <div className="mt-0.5 text-sm">{doseLabel(dose)} <span className="text-muted-foreground">· {dose.route ?? "via não informada"}</span></div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><span><Clock3 className="mr-1 inline h-3.5 w-3.5" />{elapsed(dose.actual_time ?? dose.actualTime)}</span>{stomach ? <span>· estômago {stomach}</span> : null}{dose.log_type ? <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{dose.log_type}</Badge> : null}</div>
                      {dose.notes_patient ? <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{dose.notes_patient}</div> : null}
                    </div>
                    <ChevronRight className="mt-3 h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <QuickIngestionWizard open={wizardOpen} onOpenChange={setWizardOpen} patientId={patientId} sessionId={sessionId} onSaved={onSaved} />
    </Card>
  );
}
